import './card-three-column.scss';
import './card-three-column.html.twig';

export default function () {
  const cardContainers = document.querySelectorAll('.card-three-column-container__container');

  if (!cardContainers) {
    return;
  }

  const FILTER_ALL = 'FILTER_ALL';
  const CONSULTATIONS_ALL = '';
  const CONSULTATIONS_OPEN = 'open';
  const CONSULTATIONS_CLOSED = 'closed';
  // TODO: Automatically sync with (--b-sm) custom media.
  const tabletQuery = '(min-width: 768px)';

  /**
   * CardContainer
   *
   * Handles pagination and filtering for the card-three-column component.
   */
  class CardContainer {
    constructor(node, nodeID) {
      node.id = node.id ? node.id : `card-container-${nodeID}`;
      this.node = node;
      this.noResultNotice = node.querySelector('.card-three-column-container__no-results-notice');
      this.cards = Array.from(
        node.querySelectorAll('.card-three-column-container__list > .card-three-column-container__item > *:first-child')
      );

      const filterOptionsData = node.dataset.filterOptions;
      let filterOptions = null;
      if (filterOptionsData) {
        try {
          filterOptions = JSON.parse(filterOptionsData);
        } catch (e) {
          console.error(`Unable to parse filter Options: ${e.toString()}`);
        }
      }

      // Enable Pagination?
      const paginationSettingData = node.dataset.paginationEnabled;
      const paginationEnabled = paginationSettingData === 'true';

      // Assume disabled pagination, so all cards in a single page.
      let cardsPerPage = this.cards.length;
      if (paginationEnabled) {
        // 12 on desktop, 4 on mobile.
        cardsPerPage = window.matchMedia(tabletQuery).matches ? 12 : 4;
      }

      // Settings are fixed across lifetime of Card container
      this.settings = {
        cardsPerPage,
        filterName: node.dataset.filterName,
        filterAttribute: node.dataset.filterAttribute,
        filterEnabled: node.dataset.filterEnabled,
        filterOptions,
        consultationFilterEnabled: !!node.dataset.consultationFilterEnabled,
        consultationFilterAttribute: 'data-consultation-status',
      };

      // State varies over time
      this.state = {
        page: 0,
        filter: FILTER_ALL,
        consultationFilter: CONSULTATIONS_ALL,
      };

      this.updateFilterBar();
      this.updateVisibleCards();
      this.updatePager();
    }

    /**
     * We show the filter bar, if theres enough settings information to show either the normal filter or the consultations filter.
     * @returns {boolean}
     */
    shouldShowFilterBar() {
      return (
        !!(this.settings.filterEnabled && this.settings.filterAttribute && this.settings.filterOptions) ||
        !!(this.settings.consultationFilterEnabled && this.settings.consultationFilterAttribute)
      );
    }

    /**
     * Sets the current pager page and updates.
     * @param page
     */
    setCurrentPage(page) {
      const prevPage = this.state.page;
      this.state.page = page;
      if (this.state.page !== prevPage) {
        this.updateVisibleCards(this.node.querySelector('.card-three-column-container__pager'));
        this.updatePager();
      }
    }

    /**
     * Sets the value of the filter and updates.
     * @param value
     */
    setCurrentFilter(value) {
      const prevFilter = this.state.filter;
      this.state.filter = value;
      if (this.state.filter !== prevFilter) {
        this.state.page = 0;
        this.updateVisibleCards(this.node.querySelector('.card-three-column-container__filter-bar'));
        this.updatePager();
      }
    }

    /**
     * Sets the value of the consultation filter and updates.
     * @param value
     */
    setCurrentConsultationFilter(value) {
      const prevFilter = this.state.consultationFilter;
      this.state.consultationFilter = value;
      if (this.state.consultationFilter !== prevFilter) {
        this.state.page = 0;
        this.updateVisibleCards(this.node.querySelector('.card-three-column-container__filter-bar'));
        this.updatePager();
      }
    }

    /**
     * Updates the filter bar, rerendering it, or hiding if necessary
     */
    updateFilterBar() {
      if (this.shouldShowFilterBar()) {
        const newFilterBar = this.renderFilterBar(
          this.settings.filterName,
          true,
          this.settings.filterOptions,
          this.setCurrentFilter.bind(this)
        );
        const currentFilterBar = this.node.querySelector('.card-three-column-container__filter-bar');
        if (currentFilterBar) {
          this.node.replaceChild(newFilterBar, currentFilterBar);
        } else {
          this.node.insertBefore(newFilterBar, this.node.querySelector('.card-three-column-container__list'));
        }
      }
    }

    /**
     *  Calculates the filter status (true => show, false => hide) for each of the cards in the container.
     * @returns {(boolean)[]}
     */
    calcFilterStatus() {
      const normalFilter = (card) => {
        if (this.state.filter === FILTER_ALL) {
          return true;
        }
        return card.getAttribute(this.settings.filterAttribute) === this.state.filter;
      };

      const consultationFilter = (card) => {
        if (this.state.consultationFilter === '') {
          return true;
        }

        if (this.state.consultationFilter === 'open') {
          return (
            !('consultationState' in card.dataset) ||
            card.dataset.consultationState === 'open' ||
            card.dataset.consultationState === 'opens' ||
            card.dataset.consultationState === 'opened' // Only do filtering on those with the data attribute
          );
        }

        return (
          !('consultationState' in card.dataset) || card.dataset.consultationState === this.state.consultationFilter // Only do filtering on those with the data attribute
        );
      };

      return this.cards.map((x) => {
        return normalFilter(x) && (this.settings.consultationFilterEnabled ? consultationFilter(x) : true);
      });
    }

    /**
     * Calculates visibility for each of the cards (true => show, false => hide)
     * Applies the filters (via calcFilterStatus) and then hides all but the current page.
     * @returns {boolean[]}
     */
    calcCardsVisibility() {
      const pageStart = this.state.page * this.settings.cardsPerPage;
      const pageEnd = (this.state.page + 1) * this.settings.cardsPerPage;

      // Sets a 'visible index' for each card. The visible index is the index of the card after all filters have been applied.
      // Pagination is based on the 'visible indexes' for each card.
      let visibleIndex = -1;
      const cardsVisibleIndex = this.calcFilterStatus().map((visible) => {
        if (visible) {
          visibleIndex += 1;
        }
        return {visible, visibleIndex};
      });

      return cardsVisibleIndex.map(({visible, visibleIndex}) => {
        return visibleIndex >= pageStart && visibleIndex < pageEnd && visible;
      });
    }

    /**
     * Updates the DOM to only show cards that should be visible currently.
     * @param keepPositionRelativeTo
     */
    updateVisibleCards(keepPositionRelativeTo) {
      const cardsVisibility = this.calcCardsVisibility();
      let visibleResults = false;
      const oldNodeY = keepPositionRelativeTo && keepPositionRelativeTo.getBoundingClientRect().y;
      this.cards.forEach((card, index) => {
        const cardItem = card.parentNode;
        if (!cardsVisibility[index]) {
          cardItem.classList.add('card-three-column-container__item--filtered-out');
        } else {
          cardItem.classList.remove('card-three-column-container__item--filtered-out');
          visibleResults = true;
        }
      });
      const newNodeY = keepPositionRelativeTo && keepPositionRelativeTo.getBoundingClientRect().y;
      if (keepPositionRelativeTo && newNodeY !== oldNodeY) {
        window.scrollTo(window.scrollX, window.scrollY + (newNodeY - oldNodeY));
      }

      if (!visibleResults) {
        this.noResultNotice.classList.add('card-three-column-container__no-results-notice--show');
      } else {
        this.noResultNotice.classList.remove('card-three-column-container__no-results-notice--show');
      }
    }

    /**
     * Updates the pager. Rerenders it based on the applied filter, or hides if necessary.
     */
    updatePager() {
      const numFilteredCards = this.calcFilterStatus().filter((x) => x === true).length; // All of them for now.
      const numPages =
        Math.floor(numFilteredCards / this.settings.cardsPerPage) +
        (numFilteredCards % this.settings.cardsPerPage > 0 ? 1 : 0);

      const newPager = this.renderPager(numPages, this.state.page, this.setCurrentPage.bind(this));

      const currentPager = this.node.querySelector('.card-three-column-container__pager');

      if (numPages > 1) {
        if (currentPager) {
          this.node.replaceChild(newPager, currentPager);
        } else {
          const linkContainer = this.node.querySelector('.card-three-column-container__link-container');
          if (linkContainer) {
            this.node.insertBefore(newPager, linkContainer);
          } else {
            this.node.appendChild(newPager);
          }
        }
      } else if (currentPager) {
        this.node.removeChild(currentPager);
      }
    }

    /**
     * Renders a DOM tree for the filter bar, and sets up event listeners.
     *
     * @param filterName
     * @param showFilterAll
     * @param filterOptions
     * @param onFilterChange
     * @returns {HTMLDivElement}
     */
    renderFilterBar(filterName, showFilterAll, filterOptions, onFilterChange) {
      const selectID = `card-three-column-container-filter-dropdown-select-${this.node.id}`;

      const filterBar = document.createElement('div');
      filterBar.classList.add('card-three-column-container__filter-bar');

      if (this.settings.filterEnabled) {
        const filterLabel = document.createElement('label');
        filterLabel.classList.add('card-three-column-container__filter-bar__filter-label');
        filterLabel.textContent = `Filter by ${filterName}`;
        filterLabel.htmlFor = selectID;
        filterBar.appendChild(filterLabel);

        const dropDown = document.createElement('div');
        dropDown.classList.add('card-three-column-container__filter-bar__filter-dropdown');
        filterBar.appendChild(dropDown);

        const select = document.createElement('select');
        select.classList.add('card-three-column-container__filter-bar__select');
        select.id = selectID;
        select.addEventListener('change', (event) => {
          onFilterChange(event.target.value);
        });
        dropDown.appendChild(select);

        const options = filterOptions.map(({id, label}) => {
          const option = document.createElement('option');
          option.value = id;
          option.textContent = label;
          return option;
        });
        if (showFilterAll) {
          const all = document.createElement('option');
          all.value = FILTER_ALL;
          all.textContent = 'All';
          select.appendChild(all);
        }
        options.forEach((option) => {
          select.appendChild(option);
        });
      }

      if (this.settings.consultationFilterEnabled) {
        const consultationFilterContainer = document.createElement('div');
        consultationFilterContainer.classList.add(
          'card-three-column-container__filter-bar__consultation-filter-container'
        );

        const consultationFilterLabel = document.createElement('label');
        consultationFilterLabel.classList.add('card-three-column-container__filter-bar__consultation-filter-label');
        consultationFilterLabel.textContent = 'Show consultations';
        consultationFilterContainer.appendChild(consultationFilterLabel);

        const dropDown = document.createElement('div');
        dropDown.classList.add('card-three-column-container__filter-bar__filter-dropdown');
        consultationFilterContainer.appendChild(dropDown);

        const consultationSelect = document.createElement('select');
        consultationSelect.classList.add('card-three-column-container__filter-bar__select');
        consultationSelect.addEventListener(
          'change',
          (event) => {
            const el = event.target;
            if (el.value === 'closed') {
              this.setCurrentConsultationFilter(CONSULTATIONS_CLOSED);
            } else if (el.value === 'open') {
              this.setCurrentConsultationFilter(CONSULTATIONS_OPEN);
            } else {
              this.setCurrentConsultationFilter(CONSULTATIONS_ALL);
            }
          },
          false
        );
        dropDown.appendChild(consultationSelect);
        const options = ['All', 'Open', 'Closed'];
        options.forEach(function (r) {
          consultationSelect.options.add(new Option(r, r === 'All' ? '' : r.toLowerCase()));
        });

        filterBar.appendChild(consultationFilterContainer);
      }
      return filterBar;
    }

    /**
     * Renders a DOM tree for the pager, and sets up event listeners.
     * @param totalPages
     * @param currentPage
     * @param setPage
     * @returns {HTMLUListElement}
     */
    renderPager(totalPages, currentPage, setPage) {
      const pager = document.createElement('div');
      pager.classList.add('card-three-column-container__pager');

      const pagerList = document.createElement('ul');
      pagerList.classList.add('card-three-column-container__pager-section--list');

      const pagerPrev = document.createElement('ul');
      pagerPrev.classList.add('card-three-column-container__pager-section--prev');

      const pagerNext = document.createElement('ul');
      pagerNext.classList.add('card-three-column-container__pager-section--next');

      const firstPage = 0;
      const lastPage = totalPages - 1;

      const innerWindowSize = 1; // Number of items to show adjacent to current item
      const outerWindowSize = 1; // Number of items to show from begging/end of pagination.

      const createPagerLink = (text, page, extraClasses = []) => {
        const element = document.createElement('li');
        const classes = ['card-three-column-container__pager-item', ...extraClasses];
        element.classList.add(...classes);
        const btn = document.createElement('button');
        btn.innerHTML = text;
        btn.addEventListener('click', () => setPage(page), true);
        element.appendChild(btn);
        return element;
      };

      const createPagerActive = (text) => {
        const element = document.createElement('li');
        element.classList.add('card-three-column-container__pager-item', 'card-three-column-container__pager-item--active');
        const span = document.createElement('span');
        span.innerHTML = text;
        element.appendChild(span);
        return element;
      };

      const createEllipsis = () => {
        const el = document.createElement('li');
        el.classList.add(
          'card-three-column-container__pager-item',
          'card-three-column-container__pager-item--ellipsis'
        );
        el.innerHTML = '&hellip;';
        return el;
      };

      const leftWindowPages = Array(outerWindowSize)
        .fill(firstPage)
        .map((x, y) => x + y);

      const rightWindowPages = Array(outerWindowSize)
        .fill(lastPage)
        .map((x, y) => x + y + (1 - outerWindowSize));

      const innerWindowStart = currentPage - innerWindowSize;
      const innerWindowPages = Array(innerWindowSize * 2 + 1)
        .fill(innerWindowStart)
        .map((x, y) => x + y)
        .filter((x) => x > leftWindowPages[leftWindowPages.length - 1])
        .filter((x) => x < rightWindowPages[0]);

      const previous = createPagerLink('Previous', currentPage - 1, ['card-three-column-container__pager-item--prev']);
      const leftWindow = leftWindowPages.map((x) => {
        return x === currentPage ? createPagerActive((x + 1).toString()) : createPagerLink((x + 1).toString(), x);
      });
      const rightWindow = rightWindowPages.map((x) => {
        return x === currentPage ? createPagerActive((x + 1).toString()) : createPagerLink((x + 1).toString(), x);
      });
      const innerWindow = innerWindowPages.map((x) => {
        return x === currentPage ? createPagerActive((x + 1).toString()) : createPagerLink((x + 1).toString(), x);
      });
      const next = createPagerLink('Next', currentPage + 1, ['card-three-column-container__pager-item--next']);

      const leftGap = innerWindowPages[0] - leftWindowPages[leftWindowPages.length - 1];
      const rightGap = rightWindowPages[0] - innerWindowPages[innerWindowPages.length - 1];

      const pagerItems = [
        ...leftWindow,
        ...(leftGap > 1 ? [createEllipsis()] : []),
        ...innerWindow,
        ...(rightGap > 1 ? [createEllipsis()] : []),
        ...rightWindow,
      ];

      const pagerSections = [pagerPrev, pagerList, pagerNext];

      if (currentPage > firstPage) pagerPrev.appendChild(previous);
      if (currentPage < lastPage) pagerNext.appendChild(next);

      Array.from(pagerItems).forEach((item) => pagerList.appendChild(item));
      Array.from(pagerSections).forEach((section) => pager.appendChild(section));
      return pager;
    }
  }

  // add ids here
  let idNum = 0;

  Array.from(cardContainers).forEach((cardContainer) => {
    idNum += 1;
    return new CardContainer(cardContainer, idNum);
  });
}
